import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  PLATFORM_ID,
  signal,
  ViewChild,
  inject,
  input,
  output,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { RouterLink } from '@angular/router';
import { ArrowRight, ChevronLeft, ChevronRight, LucideIconData } from 'lucide-angular';
import { Product, AddToCartEvent } from '../../../../shared/models/product.model';
import { ProductCardComponent } from '../../../../shared/ui/product-card/product-card.component';
import { ProductCardSkeletonComponent } from '../../../../shared/ui/product-card-skeleton/product-card-skeleton.component';
import { IconComponent } from '../../../../shared/ui/icon/icon.component';

const SKELETON_COUNT = Array(6);
const SLOT_STEP_FALLBACK = 216; // SSR fallback when DOM is unavailable
const AUTOPLAY_RESUME_DELAY = 8000;

@Component({
  selector: 'app-product-carousel',
  imports: [ProductCardComponent, ProductCardSkeletonComponent, IconComponent, RouterLink],
  templateUrl: './product-carousel.component.html',
  styleUrl: './product-carousel.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductCarouselComponent {
  // Existing inputs
  products = input.required<Product[]>();
  title = input('');
  loading = input(false);
  viewAllLink = input('');
  // Query params opcionales para el link "Ver todas" (p. ej. `{ on_discount: true }`).
  // Necesario para que "Ver todas" en ofertas navegue a `/tienda?on_discount=true`.
  viewAllQueryParams = input<Record<string, unknown> | null>(null);

  // New optional inputs (safe defaults — existing blocks unaffected)
  autoplay = input(false);
  autoplayMs = input(5000);
  showPagination = input(false);
  arrowsOnHover = input(false);

  // CTA opcional inyectado como slot final del carrusel ("Ver más productos
  // de X"). Activo solo si ambos campos llegan no vacíos. Los demás bloques
  // (home) lo dejan en su default vacío y se comportan exactamente igual.
  trailingLabel = input<string>('');
  trailingLink = input<string>('');

  // Outputs
  addToCart = output<AddToCartEvent>();
  zoomOpen = output<Product>();
  notifyRequest = output<Product>();
  notifyCancel = output<Product>();
  cardClick = output<Product>();
  /** Propagación del CTA "Reservar" de ProductCard (productos en precompra). */
  reserveClick = output<Product>();

  @ViewChild('track') private trackRef!: ElementRef<HTMLElement>;

  // Icons
  protected readonly chevronLeft: LucideIconData = ChevronLeft;
  protected readonly chevronRight: LucideIconData = ChevronRight;
  protected readonly arrowRight: LucideIconData = ArrowRight;
  protected readonly skeletonItems = SKELETON_COUNT;

  // Internal signals
  protected readonly currentIndex = signal(0);
  protected readonly isPaused = signal(false);
  protected readonly isReducedMotion = signal(false);
  protected readonly visibleCount = signal(1);

  // Computed
  protected readonly totalProducts = computed(() => this.products().length);
  protected readonly hasTrailingCta = computed(
    () => !!this.trailingLabel() && !!this.trailingLink(),
  );
  // El CTA cuenta como un slot adicional para que las flechas puedan
  // llegar hasta él (sin esto, `maxIndex` lo dejaría fuera del rango navegable).
  protected readonly totalSlots = computed(
    () => this.totalProducts() + (this.hasTrailingCta() ? 1 : 0),
  );
  protected readonly maxIndex = computed(() =>
    Math.max(0, this.totalSlots() - this.visibleCount()),
  );

  // Huella estable del listado de productos: solo cambia cuando la composición
  // real difiere (ids u orden), no cuando el caller pasa una nueva referencia
  // del mismo array. Sin esto, expresiones inline en plantillas (`.slice()`,
  // `?? []`) crean un array nuevo en cada CD del padre y el effect de reset
  // borra el scroll cada vez que el usuario clica una flecha.
  private readonly productsFingerprint = computed(() =>
    this.products()
      .map((p) => p.id)
      .join(','),
  );

  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private readonly destroyRef = inject(DestroyRef);
  private resumeTimerId: number | null = null;

  constructor() {
    if (this.isBrowser) {
      this.bindReducedMotionPreference();
    }

    // Autoplay effect — mirrors hero component pattern
    effect((onCleanup) => {
      const shouldAutoplay = this.autoplay();
      const ms = this.autoplayMs();
      const paused = this.isPaused();
      const reducedMotion = this.isReducedMotion();
      const isLoading = this.loading();
      const total = this.totalProducts();
      const visible = this.visibleCount();

      if (
        !shouldAutoplay ||
        !this.isBrowser ||
        paused ||
        reducedMotion ||
        isLoading ||
        total <= visible
      ) {
        return;
      }

      const timerId = window.setInterval(() => {
        this.advanceOne();
      }, ms);

      onCleanup(() => window.clearInterval(timerId));
    });

    // Reset scroll when products change (e.g. category switch). Dependemos del
    // fingerprint y NO de `products()` directo: si el padre re-pasa la misma
    // lista en otra referencia, el computed devuelve el mismo string y el
    // effect no se vuelve a disparar.
    effect(() => {
      this.productsFingerprint();
      this.currentIndex.set(0);
      if (this.isBrowser) {
        requestAnimationFrame(() => {
          this.trackRef?.nativeElement?.scrollTo({ left: 0, behavior: 'instant' as ScrollBehavior });
        });
      }
    });

    // Attach scroll listener and ResizeObserver after render
    afterNextRender(() => {
      const el = this.trackRef?.nativeElement;
      if (!el) return;

      this.updateVisibleCount(el);

      const onScroll = (): void => {
        this.onTrackScroll(el);
      };
      el.addEventListener('scroll', onScroll, { passive: true });

      let resizeObserver: ResizeObserver | null = null;
      if (typeof ResizeObserver !== 'undefined') {
        resizeObserver = new ResizeObserver(() => {
          this.updateVisibleCount(el);
        });
        resizeObserver.observe(el);
      }

      this.destroyRef.onDestroy(() => {
        el.removeEventListener('scroll', onScroll);
        resizeObserver?.disconnect();
        if (this.resumeTimerId) window.clearTimeout(this.resumeTimerId);
      });
    });
  }

  // ── Navigation ──

  protected scrollPrev(): void {
    if (!this.isBrowser) return;
    const target = Math.max(0, this.currentIndex() - this.visibleCount());
    this.scrollToProduct(target);
  }

  protected scrollNext(): void {
    if (!this.isBrowser) return;
    const target = Math.min(this.maxIndex(), this.currentIndex() + this.visibleCount());
    this.scrollToProduct(target);
  }

  protected scrollToProduct(index: number): void {
    if (!this.isBrowser) return;
    const el = this.trackRef?.nativeElement;
    if (!el) return;

    const clamped = Math.max(0, Math.min(index, this.maxIndex()));
    const slotStep = this.getSlotStep();
    el.scrollTo({ left: clamped * slotStep, behavior: 'smooth' });
    this.currentIndex.set(clamped);
    this.pauseAndScheduleResume();
  }

  // ── Pagination helpers ──

  protected isBarActive(index: number): boolean {
    return index === this.currentIndex();
  }

  protected isBarInView(index: number): boolean {
    const current = this.currentIndex();
    const visible = this.visibleCount();
    return index >= current && index < current + visible;
  }

  // ── Pause / Resume ──

  protected onWrapperMouseEnter(): void {
    if (this.autoplay()) this.isPaused.set(true);
  }

  protected onWrapperMouseLeave(): void {
    if (this.autoplay() && !this.isReducedMotion()) this.isPaused.set(false);
  }

  protected onWrapperFocusIn(): void {
    if (this.autoplay()) this.isPaused.set(true);
  }

  protected onWrapperFocusOut(event: FocusEvent): void {
    const root = this.trackRef?.nativeElement?.parentElement;
    const next = event.relatedTarget;
    if (root && next instanceof Node && root.contains(next)) return;
    if (this.autoplay() && !this.isReducedMotion()) this.isPaused.set(false);
  }

  protected onWrapperTouchStart(): void {
    if (this.autoplay()) this.isPaused.set(true);
  }

  protected onWrapperTouchEnd(): void {
    if (this.autoplay()) this.scheduleAutoplayResume();
  }

  // ── Private methods ──

  private advanceOne(): void {
    const el = this.trackRef?.nativeElement;
    if (!el) return;

    const next = this.currentIndex() + 1;
    if (next > this.maxIndex()) {
      el.scrollTo({ left: 0, behavior: 'smooth' });
      this.currentIndex.set(0);
    } else {
      const slotStep = this.getSlotStep();
      el.scrollBy({ left: slotStep, behavior: 'smooth' });
      this.currentIndex.set(next);
    }
  }

  private onTrackScroll(el: HTMLElement): void {
    const slotStep = this.getSlotStep();
    const rawIndex = Math.round(el.scrollLeft / slotStep);
    const clamped = Math.max(0, Math.min(rawIndex, this.maxIndex()));
    this.currentIndex.set(clamped);
  }

  private updateVisibleCount(el: HTMLElement): void {
    const slotStep = this.getSlotStep();
    this.visibleCount.set(Math.max(1, Math.floor(el.clientWidth / slotStep)));
  }

  private getSlotStep(): number {
    const track = this.trackRef?.nativeElement;
    const firstSlot = track?.firstElementChild as HTMLElement | null;
    if (!firstSlot) return SLOT_STEP_FALLBACK;
    const gap = parseFloat(getComputedStyle(track).gap) || 0;
    return firstSlot.offsetWidth + gap;
  }

  private pauseAndScheduleResume(): void {
    if (this.autoplay()) {
      this.isPaused.set(true);
      this.scheduleAutoplayResume();
    }
  }

  private scheduleAutoplayResume(): void {
    if (this.resumeTimerId) window.clearTimeout(this.resumeTimerId);
    this.resumeTimerId = window.setTimeout(() => {
      if (!this.isReducedMotion()) this.isPaused.set(false);
      this.resumeTimerId = null;
    }, AUTOPLAY_RESUME_DELAY);
  }

  private bindReducedMotionPreference(): void {
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    const sync = (matches: boolean): void => {
      this.isReducedMotion.set(matches);
      if (matches) this.isPaused.set(true);
    };
    sync(query.matches);

    const onChange = (e: MediaQueryListEvent): void => sync(e.matches);
    query.addEventListener('change', onChange);
    this.destroyRef.onDestroy(() => query.removeEventListener('change', onChange));
  }
}
